import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { geoEqualEarth, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import { zoom as d3zoom, zoomIdentity } from 'd3-zoom';
import { feature, merge } from 'topojson-client';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import { a2FromNumeric, binFill, binOf, fmtCount, niceBreaks } from '@/lib/geo';
import './map.css';

/*
 * World choropleth drawn locally (no tiles, no API key, no third-party request), Equal Earth
 * projection, coloured from the theme's map ramp. The country list that each page renders next
 * to it is the text alternative; the map itself is labelled as an image.
 */

let worldPromise = null;
function loadWorld() {
  if (!worldPromise) {
    worldPromise = import('world-atlas/countries-50m.json').then((mod) => {
      const topo = mod.default || mod;
      const features = feature(topo, topo.objects.countries).features
        .filter((f) => f.id !== '010') // Antarctica: no data, a fifth of the canvas
        .map((f) => ({ ...f, a2: a2FromNumeric(f.id) }));
      return { topo, features };
    });
  }
  return worldPromise;
}

function useWorld() {
  const [world, setWorld] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    loadWorld().then((w) => live && setWorld(w), (e) => live && setError(e));
    return () => { live = false; };
  }, []);
  return { topo: world?.topo || null, features: world?.features || null, error };
}

function useWidth(ref) {
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => setW(Math.round(entry.contentRect.width)));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

const TINY_AREA = 7; // px²; smaller countries with activity also get a marker so they can be seen and hovered

const Countries = memo(function Countries({ shapes, values, breaks, selected, highlight, footprintD }) {
  return (
    <>
      {shapes.map((s) => {
        const v = s.a2 ? values[s.a2] || 0 : 0;
        const cls = ['wm-c', s.a2 && v > 0 ? 'has-data' : '', selected && s.a2 === selected ? 'is-selected' : '', highlight && s.a2 === highlight ? 'is-hl' : ''].filter(Boolean).join(' ');
        return <path key={s.key} d={s.d} className={cls} data-a2={s.a2 || undefined} style={{ fill: binFill(binOf(v, breaks), breaks.length) }} />;
      })}
      {shapes.filter((s) => s.tiny && s.a2 && (values[s.a2] || 0) > 0).map((s) => (
        <circle key={`m${s.key}`} cx={s.c[0]} cy={s.c[1]} r={3.6} className={`wm-dot${selected === s.a2 ? ' is-selected' : ''}${highlight === s.a2 ? ' is-hl' : ''}`} data-a2={s.a2}
          style={{ fill: binFill(binOf(values[s.a2], breaks), breaks.length) }} />
      ))}
      {footprintD && <path d={footprintD} className="wm-fp" />}
    </>
  );
});

export function MapLegend({ label, breaks, note }) {
  return (
    <div className="wm-legend">
      <span className="wm-legend-l">{label}</span>
      <div className="wm-ramp" aria-hidden="true">
        <span className="wm-bin"><i style={{ background: 'var(--map-empty)' }} /><span>0</span></span>
        {breaks.map((b, i) => (
          <span key={b} className="wm-bin"><i style={{ background: binFill(i, breaks.length) }} /><span>{fmtCount(b)}{i === breaks.length - 1 ? '+' : ''}</span></span>
        ))}
      </div>
      {note && <span className="wm-legend-note">{note}</span>}
    </div>
  );
}

/**
 * values     { [iso2]: number } shaded on a log scale
 * footprint  [iso2] outlined on top
 * selected   iso2 highlighted; onSelect(iso2) on click
 * renderTip  (iso2, name) => node for the hover card
 * height     pixels, or 'auto' to follow the projection's proportions
 * fit        [iso2] to frame instead of the whole world (compact maps)
 * highlight  iso2 to emphasise from outside, e.g. the list row under the pointer
 */
export default function WorldMap({
  values = {}, footprint = [], selected = null, onSelect, renderTip, label, legendLabel, legendNote,
  height = 460, fit = null, compact = false, onHover, highlight = null,
}) {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const gRef = useRef(null);
  const zoomRef = useRef(null);
  const width = useWidth(wrapRef);
  const { topo, features, error } = useWorld();
  const [tip, setTip] = useState(null);

  const max = useMemo(() => Object.values(values).reduce((m, v) => Math.max(m, v || 0), 0), [values]);
  const breaks = useMemo(() => niceBreaks(max), [max]);
  const fitKey = fit ? fit.join(',') : '';
  const fpKey = footprint.join(',');
  // Without Antarctica, Equal Earth is a little over twice as wide as it is tall; "auto" sizes the canvas to the world, not the reverse.
  const h = height === 'auto' ? Math.max(200, Math.round(width * 0.46)) : height;

  const { shapes, pathFn } = useMemo(() => {
    if (!features || !width) return { shapes: [], pathFn: null };
    const proj = geoEqualEarth();
    const pad = compact ? 6 : 10;
    const target = fitKey ? features.filter((f) => fitKey.split(',').includes(f.a2)) : features;
    proj.fitExtent([[pad, pad], [width - pad, h - pad]], { type: 'FeatureCollection', features: target.length ? target : features });
    const path = geoPath(proj);
    return {
      pathFn: path,
      shapes: features.map((f, i) => ({ key: `${f.id || 'x'}-${i}`, a2: f.a2, name: f.properties?.name, d: path(f) || '', tiny: path.area(f) < TINY_AREA, c: path.centroid(f) })),
    };
  }, [features, width, h, compact, fitKey]);

  // The footprint as one outline around the merged countries, not a dashed line along every border inside it.
  const footprintD = useMemo(() => {
    if (!topo || !pathFn || !fpKey) return null;
    const want = new Set(fpKey.split(','));
    const geoms = topo.objects.countries.geometries.filter((g) => want.has(a2FromNumeric(g.id)));
    return geoms.length ? pathFn(merge(topo, geoms)) : null;
  }, [topo, pathFn, fpKey]);

  const names = useMemo(() => Object.fromEntries(shapes.filter((s) => s.a2).map((s) => [s.a2, s.name])), [shapes]);

  useEffect(() => {
    if (compact || !svgRef.current || !width) return undefined;
    const svg = select(svgRef.current);
    const z = d3zoom()
      .scaleExtent([1, 10])
      .translateExtent([[0, 0], [width, h]])
      // Plain scrolling keeps scrolling the page; Ctrl or Cmd with the wheel zooms the map.
      .filter((e) => (e.type === 'wheel' ? e.ctrlKey || e.metaKey : !e.button))
      .on('zoom', (e) => { select(gRef.current).attr('transform', e.transform); setTip(null); });
    svg.call(z);
    zoomRef.current = z;
    return () => { svg.on('.zoom', null); };
  }, [compact, width, h, features]);

  const zoomBy = useCallback((k) => {
    if (!zoomRef.current) return;
    select(svgRef.current).transition().duration(180).call(zoomRef.current.scaleBy, k);
  }, []);
  const zoomReset = useCallback(() => {
    if (!zoomRef.current) return;
    select(svgRef.current).transition().duration(220).call(zoomRef.current.transform, zoomIdentity);
  }, []);

  const onMove = (e) => {
    const a2 = e.target?.dataset?.a2;
    if (!a2) { if (tip) { setTip(null); onHover?.(null); } return; }
    const box = wrapRef.current.getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    if (!tip || tip.a2 !== a2) onHover?.(a2);
    setTip({ a2, x, y, flip: x > box.width - 240, low: y < 120 });
  };
  const onLeave = () => { setTip(null); onHover?.(null); };
  const onClick = (e) => {
    const a2 = e.target?.dataset?.a2;
    if (a2 && onSelect) onSelect(a2);
  };

  return (
    <div className={`wm${compact ? ' wm--compact' : ''}`} ref={wrapRef} style={{ height: h }}>
      {error ? <div className="wm-state">The map could not be drawn. The list shows the same figures.</div>
        : !features ? <div className="wm-state" aria-hidden="true"><span className="wm-skel" /></div>
          : (
            <svg ref={svgRef} width={width} height={h} viewBox={`0 0 ${width} ${h}`} role="img" aria-label={label}
              className={onSelect ? 'is-clickable' : undefined}>
              <g ref={gRef} onMouseMove={onMove} onMouseLeave={onLeave} onClick={onClick}>
                <Countries shapes={shapes} values={values} breaks={breaks} selected={selected} highlight={highlight} footprintD={footprintD} />
              </g>
            </svg>
          )}

      {tip && renderTip && (
        <div className={`wm-tip${tip.flip ? ' is-flip' : ''}${tip.low ? ' is-low' : ''}`} style={{ left: tip.x, top: tip.y }} role="status">
          {renderTip(tip.a2, names[tip.a2])}
        </div>
      )}

      {!compact && features && (
        <div className="wm-tools">
          <button type="button" className="wm-tool" onClick={() => zoomBy(1.6)} aria-label="Zoom in"><Plus aria-hidden="true" /></button>
          <button type="button" className="wm-tool" onClick={() => zoomBy(1 / 1.6)} aria-label="Zoom out"><Minus aria-hidden="true" /></button>
          <button type="button" className="wm-tool" onClick={zoomReset} aria-label="Reset zoom"><RotateCcw aria-hidden="true" /></button>
        </div>
      )}
      {!compact && features && legendLabel && <MapLegend label={legendLabel} breaks={breaks} note={legendNote} />}
    </div>
  );
}
